import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { FileText, Search, Share2, X } from "lucide-react";
import { useDocsAuth } from "../hooks/useDocsAuth";
import { ShareDocDialog } from "../components/ShareDocDialog";
import { getCategoryLabel, KNOWN_CATEGORY_ORDER, type DocsInventoryPage } from "../types";

export function DocsInternalLibrary() {
  const { isLoading: authLoading } = useDocsAuth();
  const [pages, setPages] = useState<DocsInventoryPage[]>([]);
  const [allPages, setAllPages] = useState<DocsInventoryPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchDocs = useCallback(async () => {
    if (authLoading) return;
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("docs_jwt");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const hasSearch = debouncedSearch.trim().length > 0;
      const hasTags = activeTags.size > 0;

      let url: string;
      if (hasSearch || hasTags) {
        const params = new URLSearchParams();
        if (hasSearch) params.set("q", debouncedSearch.trim());
        if (hasTags) params.set("tags", [...activeTags].join(","));
        url = `/api/docs/search?${params}`;
      } else {
        url = "/api/docs/inventory";
      }

      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        setPages(data.pages);
        const hasSearch = debouncedSearch.trim().length > 0;
        const hasTags = activeTags.size > 0;
        if (!hasSearch && !hasTags) {
          setAllPages(data.pages);
        }
      } else {
        setError("Failed to load documents");
      }
    } catch {
      setError("Network error loading documents");
    } finally {
      setLoading(false);
    }
  }, [authLoading, debouncedSearch, activeTags]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  function toggleTag(tag: string) {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  function clearFilters() {
    setSearch("");
    setDebouncedSearch("");
    setActiveTags(new Set());
  }

  const sorted = useMemo(() => {
    const sortKey = (t: string) => t.replace(/^Explainer:\s*/i, "").toLowerCase();
    return [...pages].sort((a, b) => sortKey(a.title).localeCompare(sortKey(b.title)));
  }, [pages]);

  // Derive tag order from full inventory (not filtered results) so filter buttons stay stable
  const tagOrder = useMemo(() => {
    const source = allPages.length > 0 ? allPages : pages;
    const allLabels = new Set(source.flatMap((p) => p.labels));
    const knownSet = new Set<string>(KNOWN_CATEGORY_ORDER);
    const newLabels = [...allLabels].filter((l) => !knownSet.has(l)).sort();
    return [...KNOWN_CATEGORY_ORDER.filter((c) => allLabels.has(c)), ...newLabels];
  }, [allPages, pages]);

  const hasActiveFilters = search.trim().length > 0 || activeTags.size > 0;

  if (loading && pages.length === 0) {
    return (
      <div className="space-y-3">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-14 animate-pulse rounded-ev bg-ev-surface-subtle" />
        ))}
      </div>
    );
  }

  if (error && pages.length === 0) {
    return (
      <div className="rounded-ev border border-ev-danger-border bg-ev-danger-bg p-4 text-sm text-ev-danger-text">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ev-text-primary">Explainers</h1>
          <p className="mt-1 text-sm text-ev-text-secondary">
            Internal view — all documents visible
          </p>
        </div>
        <ShareDocDialog pageId="" pageTitle="Explainers Library">
          {(open) => (
            <button
              onClick={open}
              className="flex items-center gap-1.5 rounded-ev border border-ev-border bg-ev-surface px-3 py-1.5 text-sm font-medium text-ev-text-primary transition-colors hover:bg-ev-surface-subtle"
            >
              <Share2 className="h-4 w-4" />
              Share Library
            </button>
          )}
        </ShareDocDialog>
      </div>

      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        <div className="rounded-ev border border-ev-border bg-ev-surface px-4 py-2">
          <div className="text-lg font-bold text-ev-text-primary">{pages.length}</div>
          <div className="text-xs text-ev-text-muted">Total</div>
        </div>
        {tagOrder.map((tag) => {
          const count = pages.filter((p) => p.labels.includes(tag)).length;
          if (count === 0) return null;
          return (
            <div key={tag} className="rounded-ev border border-ev-border bg-ev-surface px-4 py-2">
              <div className="text-lg font-bold text-ev-text-primary">{count}</div>
              <div className="text-xs text-ev-text-muted">{getCategoryLabel(tag)}</div>
            </div>
          );
        })}
      </div>

      {/* Search + filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ev-text-muted" />
          <input
            type="text"
            placeholder="Search documents by keyword..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-ev border border-ev-border bg-ev-surface py-2 pl-9 pr-3 text-sm text-ev-text-primary placeholder:text-ev-text-muted focus:border-ev-info-text focus:outline-none focus:ring-1 focus:ring-ev-info-text"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-ev-text-muted">Filter:</span>
          {tagOrder.map((tag) => {
            const isActive = activeTags.has(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-ev-info-text text-white"
                    : "bg-ev-surface-subtle text-ev-text-secondary hover:bg-ev-border"
                }`}
              >
                {getCategoryLabel(tag)}
              </button>
            );
          })}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 text-xs text-ev-text-muted hover:text-ev-text-secondary"
            >
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Document table */}
      <div className="overflow-hidden rounded-ev border border-ev-border">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-ev-border bg-ev-surface-subtle">
              <th className="px-4 py-2.5 font-medium text-ev-text-secondary">Document</th>
              <th className="hidden px-4 py-2.5 font-medium text-ev-text-secondary sm:table-cell">Category</th>
              <th className="hidden px-4 py-2.5 font-medium text-ev-text-secondary md:table-cell">Updated</th>
              <th className="w-28 px-4 py-2.5 font-medium text-ev-text-secondary" />
            </tr>
          </thead>
          <tbody>
            {sorted.map((page) => (
              <tr
                key={page.id}
                className="border-b border-ev-border last:border-b-0 transition-colors hover:bg-ev-surface-subtle"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/docs/page/${page.id}`}
                    className="flex items-center gap-2 text-ev-text-primary hover:text-ev-info-text"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-ev-text-muted" />
                    <span className="font-medium">{page.title}</span>
                  </Link>
                </td>
                <td className="hidden px-4 py-3 sm:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {page.labels.map((label) => (
                      <span
                        key={label}
                        className="inline-block rounded-full bg-ev-surface-subtle px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ev-text-muted"
                      >
                        {getCategoryLabel(label)}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="hidden px-4 py-3 text-xs text-ev-text-muted md:table-cell">
                  {page.last_updated
                    ? new Date(page.last_updated).toLocaleDateString()
                    : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <ShareDocDialog pageId={page.id} pageTitle={page.title}>
                      {(open) => (
                        <button
                          onClick={open}
                          title="Share"
                          className="inline-flex items-center rounded-ev border border-ev-border p-1 text-ev-text-muted transition-colors hover:border-ev-info-text hover:text-ev-info-text"
                        >
                          <Share2 className="h-3 w-3" />
                        </button>
                      )}
                    </ShareDocDialog>
                    {/* Edit button removed — docs are synced from Confluence, not edited in-app */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {sorted.length === 0 && (
          <div className="py-12 text-center text-sm text-ev-text-muted">
            {hasActiveFilters ? "No documents match your filters." : "No documents found."}
          </div>
        )}
      </div>

      <p className="text-xs text-ev-text-muted">
        {sorted.length} document{sorted.length !== 1 ? "s" : ""}
        {hasActiveFilters ? " matching" : ""}
        {loading && <span className="ml-1 animate-pulse">...</span>}
      </p>
    </div>
  );
}
